import { Search, Filter, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useState } from 'react';

interface ServiceFilterState {
  search: string;
  status: string; // 'all', 'active', 'inactive'
  sortBy: string; // 'name', 'sort_order', 'created'
  sortOrder: 'asc' | 'desc';
}

interface ServiceFilterProps {
  filters: ServiceFilterState;
  onFilterChange: (filters: ServiceFilterState) => void;
}

export default function ServiceFilter({ filters, onFilterChange }: ServiceFilterProps) {
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  const hasActiveFilters = () => {
    return filters.status !== 'all' || filters.sortBy !== 'name' || filters.sortOrder !== 'asc';
  };

  const clearAllFilters = () => {
    onFilterChange({
      ...filters,
      status: 'all',
      sortBy: 'name',
      sortOrder: 'asc'
    });
  };

  return (
    <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              Search Services
            </Label>
            <Input
              placeholder="Search by service name or description"
              value={filters.search}
              onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-black"
            />
          </div>

          <div className="lg:col-span-1">
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </Label>
            <Select
              value={filters.status}
              onValueChange={(value) => onFilterChange({ ...filters, status: value })}
            >
              <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus-visible:border focus-visible:border-secondary focus:ring-emerald-500 transition-all bg-white cursor-pointer">
                <SelectValue placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="lg:col-span-1">
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              Sort By
            </Label>
            <Select
              value={filters.sortBy}
              onValueChange={(value) => onFilterChange({ ...filters, sortBy: value })}
            >
              <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus-visible:border focus-visible:border-secondary focus:ring-emerald-500 transition-all bg-white cursor-pointer">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="sort_order">Sort Order</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="lg:col-span-1 flex items-end">
            <Dialog open={isFilterDialogOpen} onOpenChange={setIsFilterDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full">
                  <Filter className="w-4 h-4 mr-2" />
                  More Filters
                  {hasActiveFilters() && (
                    <span className="ml-2 bg-red-600 text-white text-xs rounded-full px-2 py-1">
                      {[filters.status !== 'all', filters.sortBy !== 'name', filters.sortOrder !== 'asc'].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between">
                    <span>Additional Filters</span>
                    {hasActiveFilters() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAllFilters}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear All
                      </Button>
                    )}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 mt-6">
                  <div>
                    <Label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order
                    </Label>
                    <Select
                      value={filters.sortOrder}
                      onValueChange={(value: 'asc' | 'desc') => onFilterChange({ ...filters, sortOrder: value })}
                    >
                      <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer">
                        <SelectValue placeholder="Select order" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending (A-Z, 1-9)</SelectItem>
                        <SelectItem value="desc">Descending (Z-A, 9-1)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => setIsFilterDialogOpen(false)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Apply Filters
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
